import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { Metric, MetricValue, ApiResponse } from '@/types'

// Fetch all metrics
export function useMetrics() {
  return useQuery({
    queryKey: ['metrics'],
    queryFn: async (): Promise<Metric[]> => {
      const res = await fetch('/api/metrics')
      const data: ApiResponse<Metric[]> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to fetch metrics')
      return data.data!
    },
  })
}

// Fetch single metric with values
export function useMetric(id: string | undefined) {
  return useQuery({
    queryKey: ['metrics', id],
    queryFn: async (): Promise<Metric & { values: MetricValue[] }> => {
      const res = await fetch(`/api/metrics/${id}`)
      const data: ApiResponse<Metric & { values: MetricValue[] }> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to fetch metric')
      return data.data!
    },
    enabled: !!id,
  })
}

// Create metric
export function useCreateMetric() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (metric: Partial<Metric>) => {
      const res = await fetch('/api/metrics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(metric),
      })
      const data: ApiResponse<Metric> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to create metric')
      return data.data!
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metrics'] })
      queryClient.invalidateQueries({ queryKey: ['scorecard'] })
    },
  })
}

// Update metric
export function useUpdateMetric() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...metric }: Partial<Metric> & { id: string }) => {
      const res = await fetch(`/api/metrics/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(metric),
      })
      const data: ApiResponse<Metric> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to update metric')
      return data.data!
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['metrics'] })
      queryClient.invalidateQueries({ queryKey: ['metrics', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['scorecard'] })
    },
  })
}

// Delete metric
export function useDeleteMetric() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/metrics/${id}`, { method: 'DELETE' })
      const data: ApiResponse<void> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to delete metric')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metrics'] })
      queryClient.invalidateQueries({ queryKey: ['scorecard'] })
    },
  })
}

// Fetch metric values
export function useMetricValues(metricId: string | undefined) {
  return useQuery({
    queryKey: ['metrics', metricId, 'values'],
    queryFn: async (): Promise<MetricValue[]> => {
      const res = await fetch(`/api/metrics/${metricId}/values`)
      const data: ApiResponse<MetricValue[]> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to fetch values')
      return data.data!
    },
    enabled: !!metricId,
  })
}

// Add metric value (admin)
export function useAddMetricValue() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ metricId, value, recorded_at }: { metricId: string; value: number; recorded_at: number }) => {
      const res = await fetch(`/api/metrics/${metricId}/values`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value, recorded_at, source: 'manual' }),
      })
      const data: ApiResponse<{ id: string }> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to add value')
      return data.data!
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['metrics', variables.metricId] })
      queryClient.invalidateQueries({ queryKey: ['metrics', variables.metricId, 'values'] })
      queryClient.invalidateQueries({ queryKey: ['scorecard'] })
    },
  })
}

// Update metric value (admin)
export function useUpdateMetricValue() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ metricId, valueId, value, recorded_at }: { metricId: string; valueId: string; value?: number; recorded_at?: number }) => {
      const res = await fetch(`/api/metrics/${metricId}/values/${valueId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value, recorded_at }),
      })
      const data: ApiResponse<void> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to update value')
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['metrics', variables.metricId] })
      queryClient.invalidateQueries({ queryKey: ['metrics', variables.metricId, 'values'] })
      queryClient.invalidateQueries({ queryKey: ['scorecard'] })
    },
  })
}

// Delete metric value (admin)
export function useDeleteMetricValue() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ metricId, valueId }: { metricId: string; valueId: string }) => {
      const res = await fetch(`/api/metrics/${metricId}/values/${valueId}`, { method: 'DELETE' })
      const data: ApiResponse<void> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to delete value')
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['metrics', variables.metricId] })
      queryClient.invalidateQueries({ queryKey: ['metrics', variables.metricId, 'values'] })
      queryClient.invalidateQueries({ queryKey: ['scorecard'] })
    },
  })
}
